import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play } from "lucide-react";
import { IMAGES } from "../lib/images";
import { Reveal, useSEO } from "../components/site/Reveal";
import Newsletter from "../components/site/Newsletter";
import api from "../lib/api";

const CATEGORY_ORDER = ["Portraits", "Art & Sculpture", "Textiles", "Festivals", "Regalia", "Food & Market"];

export default function Gallery() {
  useSEO(
    "Gallery",
    "A visual gallery of Yorùbá cultural heritage — portraits, art and sculpture, textiles, festivals, regalia, and market life."
  );
  const [gallery, setGallery] = React.useState([]);
  const [active, setActive] = React.useState("All");
  const [banner, setBanner] = React.useState("");
  React.useEffect(() => { api.get("/site-settings").then((r) => { if (r.data?.banner_gallery) setBanner(r.data.banner_gallery); }).catch(() => {}); }, []);
  const [lightbox, setLightbox] = React.useState(null);

  React.useEffect(() => {
    api.get("/gallery").then((r) => setGallery(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const cats = React.useMemo(() => {
    const present = [];
    gallery.forEach((g) => {
      if (g.category && !present.includes(g.category)) present.push(g.category);
    });
    const ordered = CATEGORY_ORDER.filter((c) => present.includes(c));
    const extras = present.filter((c) => !CATEGORY_ORDER.includes(c));
    return ["All", ...ordered, ...extras];
  }, [gallery]);

  const items = (active === "All" ? gallery : gallery.filter((g) => g.category === active)).map((g) => ({
    src: g.src,
    cat: g.category,
    caption: g.caption,
    type: g.type || "image",
    poster: g.poster || "",
  }));

  return (
    <div>
      {/* HERO */}
      <header className="relative min-h-[48vh] flex items-end grain-overlay">
        <div className="absolute inset-0">
          <img src={banner || IMAGES.festivalCrowd} alt="A Yorùbá festival procession of devotees in white by a sacred river" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-night via-night/70 to-night/40" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 pb-14 w-full">
          <p className="uppercase tracking-[0.3em] text-xs text-gold-soft mb-5">Visual Heritage</p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-serif text-5xl lg:text-6xl text-cream leading-tight text-shadow-hero"
          >
            Gallery
          </motion.h1>
        </div>
      </header>

      {/* FILTERS */}
      <div className="sticky top-[102px] z-30 bg-cream/90 backdrop-blur-md border-b border-parchment">
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-wrap gap-2">
          {cats.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`gallery-filter-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                active === c
                  ? "bg-forest text-cream"
                  : "border border-parchment text-ink-muted hover:border-gold hover:text-forest"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* MASONRY GRID */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          {items.map((item, i) => (
            <motion.button
              key={item.src + i}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
              onClick={() => setLightbox(item)}
              data-testid={`gallery-item-${i}`}
              className="group relative mb-5 block w-full overflow-hidden rounded-xl border border-parchment break-inside-avoid"
            >
              {item.type === "video" ? (
                <div className="relative w-full bg-night">
                  <video
                    src={item.src}
                    poster={item.poster || undefined}
                    muted
                    playsInline
                    preload="metadata"
                    className="w-full object-cover"
                  />
                  <span className="absolute inset-0 grid place-items-center pointer-events-none">
                    <span className="grid place-items-center w-14 h-14 rounded-full bg-night/60 text-cream group-hover:bg-gold group-hover:text-night transition-colors">
                      <Play className="w-6 h-6 ml-0.5" />
                    </span>
                  </span>
                </div>
              ) : (
                <img
                  src={item.src}
                  alt={item.caption}
                  loading="lazy"
                  className="w-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-night/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-0 left-0 p-5 text-left translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all">
                <p className="text-xs uppercase tracking-widest text-gold-soft mb-1">{item.cat}</p>
                <p className="font-serif text-lg text-cream">{item.caption}</p>
              </div>
            </motion.button>
          ))}
        </div>
      </section>

      {/* LIGHTBOX */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-night/90 backdrop-blur-sm grid place-items-center p-6"
            onClick={() => setLightbox(null)}
            data-testid="gallery-lightbox"
          >
            <button
              className="absolute top-5 right-5 grid place-items-center w-11 h-11 rounded-full bg-cream/10 text-cream hover:bg-gold hover:text-night transition-colors"
              onClick={() => setLightbox(null)}
              data-testid="gallery-lightbox-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <motion.figure
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {lightbox.type === "video" ? (
                <video src={lightbox.src} poster={lightbox.poster || undefined} controls autoPlay playsInline className="w-full max-h-[78vh] rounded-lg bg-night" />
              ) : (
                <img src={lightbox.src} alt={lightbox.caption} className="w-full max-h-[78vh] object-contain rounded-lg" />
              )}
              <figcaption className="text-center mt-4">
                <p className="text-xs uppercase tracking-widest text-gold-soft mb-1">{lightbox.cat}</p>
                <p className="font-serif text-xl text-cream">{lightbox.caption}</p>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>

      <Newsletter />
    </div>
  );
}
